import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { combineLatest, filter, map, take, tap } from "rxjs";
import { IColumnDefinition } from "@common/models/column-definition";
import {
  addColumnFieldsToAdminForm,
  addFilterFieldsToAdminForm,
  getDefaultWidgetConfigAdminForm,
} from "@feature/widget-admin/services/widget-admin-form";
import {
  WidgetConfiguration,
  WidgetConfigurationsService,
} from "@feature/widget-configs";
import { WidgetAdminHandler } from "@widget-admin/services/widget-admin-handler";
import { FormDefinition  } from '@feature/_app_modules/dynamic-forms/dynamic-form';
import { mapWherePropertiesMatch } from "@common/index";
import { CurrencyService } from "@currency/services/currency.service";
import { Currency } from "@currency/models/currency.model";
import { AdminPaymentTableFilterFieldProvider } from "@payments-to-you/filters/admin-payment-table-filter-field-provider";
import { QueryBuilderConfigProvider } from "@common/filter/query-builder-config-provider";
import { QueryBuilderConfig } from "ngx-angular-query-builder";
import { PaymentsToYouService } from "@feature/payments-to-you/services/payments-to-you.service";

@Injectable({
  providedIn: "root",
})
export class PaymentsToYouWidgetAdminHandler implements WidgetAdminHandler {
  private availableColumnDefinitions: IColumnDefinition[];
  private queryConfig: QueryBuilderConfig;

  constructor(
    private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly currencyService: CurrencyService,
    private readonly paymentsToYouService: PaymentsToYouService,
  ) {}

  initialise(): Observable<boolean> {
    this.paymentsToYouService.loadAllColumns();

    const columnConfig = this.paymentsToYouService.getAllColumns().pipe(
      filter((s) => s != undefined),
      tap((columnDefs: IColumnDefinition[]) => {
        this.availableColumnDefinitions = columnDefs;
      }),
      take(1),
    );

    const filterConfig = this.currencyService.getCurrencies().pipe(
      map((currencies: Currency[]) => {
        const filterFieldProvider = new AdminPaymentTableFilterFieldProvider(
          currencies,
        );
        const configProvider = new QueryBuilderConfigProvider(
          filterFieldProvider,
        );
        this.queryConfig = configProvider.getQueryBuilderConfig();
      }),
      take(1),
    );

    const source = combineLatest([columnConfig, filterConfig])
    return source.pipe(map((r) => true));

  }

  getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition {
    let formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);

    formDefinition = addColumnFieldsToAdminForm(
      selectedConfig,
      this.availableColumnDefinitions,
      formDefinition,
    );

    formDefinition = addFilterFieldsToAdminForm(
      selectedConfig,
      this.queryConfig,
      formDefinition,
    );

    return formDefinition;
  }

  createCopyFromAdminForm(
    selectedConfig: WidgetConfiguration,
    adminFormValue: any,
  ): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(
      selectedConfig,
      adminFormValue,
    );

    return this.widgetConfigService.createcopy(copyOfSelectedConfig).pipe(
      map((r) => true),
    );
  }

  saveConfigFromAdminForm(
    selectedConfig: WidgetConfiguration,
    adminFormValue: any,
  ): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(
      selectedConfig,
      adminFormValue,
    );

    return this.widgetConfigService.save(copyOfSelectedConfig).pipe(
      map((r) => true),
    );
  }

  deleteConfigFromAdminForm(
    selectedConfig: WidgetConfiguration,
    adminFormValue: any,
  ): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(
      selectedConfig,
      adminFormValue,
    );

    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(
      map((r) => true),
    );
  }
}
